import type { ReactNode } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { HelpBulb } from '@/features/help';
import { isSessionRenewalError } from '@/shared/api';
import { signedOut } from '@/shared/session';
import { Button, ErrorScreen } from '@/shared/ui';
import { useGetMetaQuery } from '../api/portalApi';
import { usePeriod, type Period } from '../model/usePeriod';
import { usePortalLoad } from '../model/usePortalLoad';
import { FilterBar } from './FilterBar';
import { PortalLoadProgress } from './PortalLoadProgress';

interface PortalPageProps {
  title: string;
  /** Help URLs of the page (its screen topic first), shown as a bulb after the title. */
  help?: readonly string[];
  /** Show the global filter toolbar (all report pages except Profile); `period`: the range picker only. */
  filters?: boolean | 'period';
  /** Header actions instead of the filters (record forms: back and explorer links). */
  actions?: ReactNode;
  /** Content; receives the resolved global filters once the reference data is loaded. */
  children: (period: Period) => ReactNode;
}

/** `header.page-header` + `div.content`; the header and filters render before the data. */
export function PortalPage({ title, help, filters = true, actions, children }: PortalPageProps) {
  const period = usePeriod();
  // Same cache entry as usePeriod: reports why `meta` (and so every page) has no data.
  const { error, refetch } = useGetMetaQuery(undefined);
  // `meta` waits for the backend's data load: show its live progress meanwhile, also over a failed request (a proxy
  // timeout) while the load still runs.
  const load = usePortalLoad(!period);
  return (
    <>
      <header className="page-header">
        <div>
          <div className="page-title-row">
            <h1 className="page-title">{title}</h1>
            {help && <HelpBulb topics={help} label={`${title} page`} />}
          </div>
        </div>
        {filters && period && <FilterBar period={period} periodOnly={filters === 'period'} />}
        {!filters && actions && <div className="toolbar">{actions}</div>}
      </header>
      <div className="content" id="content">
        {period ? (
          children(period)
        ) : error && load?.state !== 'loading' ? (
          <MetaError error={error} onRetry={() => void refetch()} />
        ) : (
          <PortalLoadProgress status={load} />
        )}
      </div>
    </>
  );
}

/** The reference data failed: say why, offer Retry, and a fresh sign-in when the session is gone. */
function MetaError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const dispatch = useAppDispatch();
  const sessionLost = isSessionRenewalError(error);
  return (
    <ErrorScreen
      title={sessionLost ? 'Session could not be renewed' : 'Portal data could not be loaded'}
      error={error}
      onRetry={onRetry}
      actions={
        sessionLost && (
          <Button variant="link" onClick={() => dispatch(signedOut())}>
            Sign in again
          </Button>
        )
      }
    />
  );
}
